
import { useState, useEffect } from 'react';
import { DeviceFormData, ValidationErrors } from './types';
import { isValidEmail, isValidIPAddress, isValidPort } from './utils/validation';

const STORAGE_KEY = 'device-form-data';

export const useDeviceForm = (onComplete: () => void) => {
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});

  // Initialize form data from localStorage if available
  const [formData, setFormData] = useState<DeviceFormData>(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      try {
        return JSON.parse(savedData);
      } catch (e) {
        console.error('Error parsing saved form data:', e);
      }
    }
    
    // Default initial state if no saved data exists
    return {
      // Device Profile Details
      name: '',
      department: '',
      type: '',
      category: '',
      serial: '',
      location: '',
      description: '',
      // Transport Configuration
      transportType: '',
      protocol: '',
      ipAddress: '',
      port: '',
      // Alarm Rules
      alarmThreshold: '',
      notificationEmail: '',
      // Device Provisioning
      provisioningKey: '',
      activationCode: ''
    };
  });
  
  // Save form data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);
  
  // Available protocols based on transport type
  const [availableProtocols, setAvailableProtocols] = useState<string[]>([]);
  
  // Update available protocols when transport type changes
  useEffect(() => {
    switch(formData.transportType) {
      case 'MQTT':
        setAvailableProtocols(['MQTT 3.1', 'MQTT 3.1.1', 'MQTT 5.0']);
        break;
      case 'CoAP':
        setAvailableProtocols(['CoAP UDP', 'CoAP TCP', 'CoAP DTLS']);
        break;
      case 'LWM2M':
        setAvailableProtocols(['LWM2M 1.0', 'LWM2M 1.1', 'LWM2M over CoAP']);
        break;
      case 'SNMP':
        setAvailableProtocols(['SNMPv1', 'SNMPv2c', 'SNMPv3']);
        break;
      case 'Default':
        setAvailableProtocols(['HTTP', 'HTTPS', 'TCP', 'UDP']);
        break;
      default:
        setAvailableProtocols([]);
    }
    
    // Reset protocol selection when transport type changes
    setFormData(prev => ({
      ...prev,
      protocol: ''
    }));
  }, [formData.transportType]);

  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'ipAddress':
        return !isValidIPAddress(value) ? 'Please enter a valid IP address' : undefined;
      case 'port':
        return !isValidPort(value) ? 'Please enter a valid port number (0-65535)' : undefined;
      case 'notificationEmail':
        return !isValidEmail(value) ? 'Please enter a valid email address' : undefined;
      default:
        return undefined;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Validate field if it's one we're watching
    if (['ipAddress', 'port', 'notificationEmail'].includes(name)) {
      const error = validateField(name, value);
      
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const validateStep = (currentStep: number): boolean => {
    switch(currentStep) {
      case 1:
        return !!(formData.name && formData.department && formData.type && formData.category && formData.serial);
      case 2:
        // If IP address or port are provided, they must be valid
        const hasIpError = formData.ipAddress && !isValidIPAddress(formData.ipAddress);
        const hasPortError = formData.port && !isValidPort(formData.port);
        return !(hasIpError || hasPortError);
      case 3:
        // If email is provided, it must be valid
        return !(formData.notificationEmail && !isValidEmail(formData.notificationEmail));
      case 4:
        return true; // Optional step
      default:
        return false;
    }
  };

  const resetForm = () => {
    // Clear saved form data
    localStorage.removeItem(STORAGE_KEY);
    
    setFormData({
      name: '',
      department: '',
      type: '',
      category: '',
      serial: '',
      location: '',
      description: '',
      transportType: '',
      protocol: '',
      ipAddress: '',
      port: '',
      alarmThreshold: '',
      notificationEmail: '',
      provisioningKey: '',
      activationCode: ''
    });
    setShowSuccessDialog(false);
    setErrors({});
  };

  const handleComplete = () => {
    resetForm();
    onComplete();
  };

  return {
    formData,
    handleChange,
    validateStep,
    isSubmitting,
    setIsSubmitting,
    showSuccessDialog,
    setShowSuccessDialog,
    handleComplete,
    availableProtocols,
    resetForm,
    errors
  };
};
